import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationCancel, NavigationEnd, NavigationError, Router, RouterOutlet } from '@angular/router';
import { LayoutService } from '../../core/services/layout.service';
import { TopbarKendoComponent } from '../topbar/topbar-kendo.component';
import { SidebarComponent } from '../sidebar/sidebar.component';
import { ReactAppWrapperComponent } from '../../shared/components/react-app-wrapper/react-app-wrapper.component';
import { SidebarMenuItem } from '../sidebar/sidebar-menu-item.interface';
import { Store } from '@ngrx/store';
import { selectFilteredSidebarMenuItems } from '../../store/entities/menu/menu.selectors';
import { selectFeaturesDecisionReady, selectUseReactUI } from '../../store/entities/features/features.selectors';
import { Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, startWith } from 'rxjs/operators';

const ANGULAR_ROUTES = ['/links', '/account', '/admin/tags', '/admin/users', '/admin/link-templates', '/admin/qr-templates', '/admin/pending-links', '/admin/attributes', '/admin/trusted-destinations', '/admin/ip-exclusions', '/admin/e-link', '/power-pages'];

@Component({
    selector: 'app-main-layout',
    imports: [
        CommonModule,
        RouterOutlet,
        TopbarKendoComponent,
        SidebarComponent,
        ReactAppWrapperComponent,
    ],
    templateUrl: './main-layout.component.html',
    styleUrl: './main-layout.component.css',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MainLayoutComponent {
    private layoutService = inject(LayoutService);
    private store = inject(Store);
    private router = inject(Router);

    isSidebarExpanded = this.layoutService.isSidebarExpanded;
    menuItems$: Observable<SidebarMenuItem[]> = this.store.select(selectFilteredSidebarMenuItems);

    showAngularContent$: Observable<boolean> = this.router.events.pipe(
      // React to any navigation that settles the URL, not just successful ones.
      // A failed/cancelled navigation (e.g. a lazy-load hiccup or guard redirect)
      // must not leave the React fallback showing on an Angular route.
      filter(
        (event) =>
          event instanceof NavigationEnd ||
          event instanceof NavigationCancel ||
          event instanceof NavigationError
      ),
      // Always derive from the router's current URL after it has settled.
      map(() => this.router.url),
      startWith(this.router.url),
      map((url) => ANGULAR_ROUTES.some((route) => url.startsWith(route))),
      distinctUntilChanged()
    );

    shouldUseReactUI$ = this.store.select(selectUseReactUI).pipe(
        distinctUntilChanged()
    );

    isReactShellReady$ = this.store.select(selectFeaturesDecisionReady).pipe(
        distinctUntilChanged()
    );

    onSidebarExpandedChange(expanded: boolean): void {
        this.layoutService.setSidebarExpanded(expanded);
    }
}
